import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { useUserStore } from '@store/user';
import type { MaybeRefOrGetter, Ref } from 'vue';

export type MyOrderAction = App.Data.Responses.Me.MyOrderActionData;

export function useMyOrderActions(date: Ref<string>, options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
    const userStore = useUserStore();
    const enabled = computed(() => toValue(options.enabled ?? true));

    const { data, isLoading, isFetching, refetch, error } = useQuery({
        queryKey: computed(() => ['me/order-actions', date.value, userStore.tenantId]),
        queryFn: async (): Promise<MyOrderAction[]> => {
            const resp = await window.tenantAxios.get<{ data: MyOrderAction[] }>('/me/order-actions', {
                params: { date: date.value },
            });
            return resp.data.data || [];
        },
        enabled,
        retry: false,
        placeholderData: [],
        staleTime: 5000,
    });

    return {
        data,
        isLoading,
        isFetching,
        refetch,
        error,
    };
}

export function useToggleOrderActionCompleted() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { id: number; completed: boolean }) => {
            const resp = await window.tenantAxios.patch<{ data: { id: number; completed_at: string | null } }>(
                `/me/order-actions/${input.id}`,
                { completed: input.completed },
            );
            return resp.data.data;
        },
        onMutate: async (input) => {
            await queryClient.cancelQueries({ queryKey: ['me/order-actions'] });

            const snapshots = queryClient.getQueriesData<MyOrderAction[]>({ queryKey: ['me/order-actions'] });
            const optimisticCompletedAt = input.completed ? new Date().toISOString() : null;

            queryClient.setQueriesData<MyOrderAction[]>({ queryKey: ['me/order-actions'] }, (old) => {
                if (!old) return old;
                return old.map((action) =>
                    action.id === input.id ? { ...action, completed_at: optimisticCompletedAt } : action,
                );
            });

            return { snapshots };
        },
        onError: (_err, _input, ctx) => {
            ctx?.snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['me/order-actions'] });
        },
    });
}
